import { playerPrizes } from 'api/lotteries';
import type { Dispatch } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { fetchWonLottery } from './helpers';
import type { Action } from './reducer';
import { ACTION_TYPE } from './reducer';
import { getSeenWinIds, saveSeenWinId } from './seenWins';

interface IUseLotteryWinsParams {
  dispatch: Dispatch<Action>;
  userId: number | undefined;
  isProjectLoaded: boolean;
  isServerStorageEnabled: boolean;
}

export const useLotteryWins = ({
  dispatch,
  userId,
  isProjectLoaded,
  isServerStorageEnabled,
}: IUseLotteryWinsParams) => {
  // drops a check that finishes after a logout
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  /** /api/prizes, not the statuses: SS may award a prize without a ticket */
  const checkWins = useCallback(async () => {
    if (!(userId && isProjectLoaded)) return;

    const seenIdsRequest = getSeenWinIds(userId, isServerStorageEnabled);
    const prizes = await playerPrizes()
      .then(({ data }) => data)
      .catch(() => []);

    const lotteryPrizes = prizes.filter(
      ({ contestType }) => contestType === 'Lottery'
    );

    // the winners table waits for it, the popups can wait for the rest
    if (userIdRef.current === userId) {
      dispatch({
        type: ACTION_TYPE.SET_WON_PRIZES,
        payload: { prizeIds: lotteryPrizes.map(({ id }) => id) },
      });
    }

    const seenIds = await seenIdsRequest;
    const unseenIds = Array.from(
      new Set(lotteryPrizes.map(({ contestId }) => contestId))
    ).filter((id) => !seenIds.includes(id));

    const found = (await Promise.all(unseenIds.map(fetchWonLottery))).filter(
      (item): item is NonNullable<typeof item> => item !== null
    );

    if (userIdRef.current !== userId) return;

    dispatch({
      type: ACTION_TYPE.ADD_SEEN_WINS,
      payload: { lotteryIds: seenIds },
    });
    dispatch({
      type: ACTION_TYPE.UPDATE_LOTTERIES,
      payload: { lotteries: found.map(({ lottery }) => lottery) },
    });
    dispatch({
      type: ACTION_TYPE.SET_STATUSES,
      payload: { statuses: found.map(({ status }) => status) },
    });
    dispatch({
      type: ACTION_TYPE.ENQUEUE_WINS,
      payload: { lotteryIds: found.map(({ lottery }) => lottery.id) },
    });
  }, [dispatch, userId, isProjectLoaded, isServerStorageEnabled]);

  // wins of the draws that happened while the player was away
  useEffect(() => {
    checkWins();
  }, [checkWins]);

  const markWinSeen = useCallback(
    (lotteryId: number) => {
      dispatch({ type: ACTION_TYPE.MARK_WIN_SEEN, payload: { lotteryId } });

      if (userId) void saveSeenWinId(userId, isServerStorageEnabled, lotteryId);
    },
    [dispatch, userId, isServerStorageEnabled]
  );

  const dropWin = useCallback(
    (lotteryId: number) => {
      dispatch({ type: ACTION_TYPE.DROP_WIN, payload: { lotteryId } });
    },
    [dispatch]
  );

  return { checkWins, markWinSeen, dropWin };
};
